import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../features/auth/stores/auth.store';

const apiBaseUrl =
  import.meta.env.MODE === 'development'
    ? '/proxy-api'
    : (window as any).__ENV__?.API_BASE_URL || 'http://PLEASE_SET_API_BASE_URL';

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 统一的清除认证信息并跳转登录页
 */
const clearAuthAndRedirectToLogin = (message?: string) => {
  // 清除 localStorage
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');

  // 清除 zustand store 状态
  useAuthStore.getState().clearAuth();

  // 如果有提示消息，保存到 sessionStorage，登录页可以显示
  if (message) {
    sessionStorage.setItem('login_redirect_message', message);
  }

  // 使用 window.location 强制刷新页面并清除所有状态
  window.location.href = '/login';
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    const statusCode = error.response?.status;
    const errorData = error.response?.data;
    const errorMessage = errorData?.message || error.message || 'Unknown error';

    // 统一处理 401 Unauthorized
    if (statusCode === 401) {
      console.warn('[API] 401 Unauthorized - clearing auth and redirecting to login');
      clearAuthAndRedirectToLogin('登录已过期，请重新登录');
      return Promise.reject(error);
    }

    // 处理 404 Not Found - 特殊情况：用户/管理员不存在（token 过期或用户被删除）
    if (statusCode === 404) {
      const isUserNotFoundError =
        errorMessage.includes('Member with ID') ||
        errorMessage.includes('member not found') ||
        errorMessage.includes('Admin with ID') ||
        errorMessage.includes('admin not found') ||
        errorMessage.includes('用户不存在') ||
        errorMessage.includes('管理员不存在');

      if (isUserNotFoundError) {
        console.warn('[API] 404 User not found - clearing auth and redirecting to login');
        clearAuthAndRedirectToLogin('用户信息不存在，请重新登录');
        return Promise.reject(error);
      }
    }

    // 处理 403 Forbidden - 权限不足
    if (statusCode === 403) {
      console.warn('[API] 403 Forbidden - insufficient permissions');
      // 不清除认证信息，只提示权限不足
      // 用户可能只是没有访问某个资源的权限，但仍然登录着
    }

    // 处理 500 Internal Server Error
    if (statusCode === 500) {
      console.error('[API] 500 Internal Server Error:', errorMessage);
    }

    // 其他错误直接抛出，由具体业务逻辑处理
    return Promise.reject(error);
  }
);

export default api;
